// 게시판 상세 조회 모달 형식으로

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { AdminBoardItemDto } from "@/dto/AdminBoardDto";
import { Comment } from "../Comment";

interface DetailProps {
  open: boolean;
  onClose: () => void;
  boardItem: AdminBoardItemDto;
  isLoading: boolean;
  admIdntfcId?: string;
}

export const Detail = ({
  open,
  onClose,
  boardItem,
  isLoading,
  admIdntfcId,
}: DetailProps) => {
  const theme = useTheme();

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "yyyy년 MM월 dd일 HH:mm", {
        locale: ko,
      });
    } catch (error) {
      return "-";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="board-detail-dialog-title"
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle id="board-detail-dialog-title">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" component="div" fontWeight={600}>
                {boardItem.boardTitle}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            {/* 게시물 메타 정보 */}
            <Grid
              container
              spacing={2}
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
              }}
            >
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    작성자:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {boardItem.admName}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    작성일:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatDate(boardItem.regDttm)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CategoryIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    페이지/섹션:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                      label={boardItem.pageCategoryName}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                    <Chip
                      label={boardItem.sectionCategoryName}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                      }}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <VisibilityIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    조회수:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {boardItem.viewCount}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            {/* 게시물 내용 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                내용
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  minHeight: "100px",
                }}
              >
                {boardItem.boardContent &&
                boardItem.boardContent !== "undefined" ? (
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {boardItem.boardContent}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    내용이 없습니다.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* 첨부 파일 */}
            {boardItem.fileNms && boardItem.fileNms.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  첨부 파일 ({boardItem.fileNms.length}개)
                </Typography>
                <ImageList
                  sx={{ width: "100%", maxHeight: 400 }}
                  cols={boardItem.fileNms.length > 1 ? 2 : 1}
                  gap={8}
                >
                  {boardItem.fileNms.map((fileUrl, index) => (
                    <ImageListItem
                      key={index}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={fileUrl}
                        alt={`첨부파일 ${index + 1}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "300px",
                          objectFit: "contain",
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )}

            {/* 댓글 섹션 */}
            <Comment boardId={boardItem.boardId} admIdntfcId={admIdntfcId} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              닫기
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
